import { useBrowserStore, SnapshotElement, BrowserAction, PageContext, RegionType, Region } from '@/stores/browserStore';
import {
  ChevronDown,
  ChevronRight,
  MousePointer,
  Link2,
  ToggleLeft,
  TextCursorInput,
  List,
  FileText,
  Play,
  Send,
  X,
  Upload,
  Search,
  MapPin,
  Compass,
  LayoutGrid,
  Settings,
  ListOrdered,
  FormInput,
  BarChart3,
  FileText as ContentIcon,
  PanelLeft,
  AppWindow,
  MessageSquare,
  Columns,
  Globe,
  Layers,
} from 'lucide-react';
import { useState, useMemo, useRef } from 'react';

interface GroupedElements {
  buttons: SnapshotElement[];
  links: SnapshotElement[];
  toggles: SnapshotElement[];
  inputs: SnapshotElement[];
  selects: SnapshotElement[];
  other: SnapshotElement[];
}

function categorizeElement(el: SnapshotElement): keyof GroupedElements {
  const tag = el.tag.toLowerCase();
  const type = el.type?.toLowerCase() ?? '';
  const role = el.role?.toLowerCase() ?? '';

  // Links
  if (tag === 'a' || role === 'link') return 'links';

  // Toggles/checkboxes/radios - clickable selection controls
  if (type === 'checkbox' || type === 'radio' || role === 'switch' || role === 'checkbox' || role === 'radio') return 'toggles';

  // Inputs (text-based inputs only)
  if ((tag === 'input' && type !== 'checkbox' && type !== 'radio' && type !== 'submit' && type !== 'button') || tag === 'textarea') return 'inputs';

  // Selects
  if (tag === 'select' || role === 'combobox' || role === 'listbox') return 'selects';

  // Buttons
  if (tag === 'button' || role === 'button' || type === 'submit' || type === 'button') return 'buttons';

  return 'other';
}

function getElementLabel(el: SnapshotElement): string {
  return el.ariaLabel || el.text?.trim() || el.placeholder || el.name || el.ref;
}

const CATEGORY_CONFIG = {
  buttons: { label: 'Buttons', icon: MousePointer, color: 'text-blue-400' },
  links: { label: 'Links', icon: Link2, color: 'text-purple-400' },
  toggles: { label: 'Toggles', icon: ToggleLeft, color: 'text-green-400' },
  inputs: { label: 'Inputs', icon: TextCursorInput, color: 'text-orange-400' },
  selects: { label: 'Selects', icon: List, color: 'text-cyan-400' },
  other: { label: 'Other', icon: FileText, color: 'text-muted-foreground' },
};

function InputElement({
  el,
  onSubmit,
  onFileUpload,
}: {
  el: SnapshotElement;
  onSubmit: (el: SnapshotElement, value: string) => void;
  onFileUpload: (el: SnapshotElement, file: File) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isFileInput = el.type === 'file';

  const handleSubmit = () => {
    if (inputValue.trim()) {
      onSubmit(el, inputValue.trim());
      setInputValue('');
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setInputValue('');
    setIsEditing(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileUpload(el, file);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    // File input mode
    if (isFileInput) {
      return (
        <div className="flex flex-col gap-1.5 px-2 py-1.5 rounded bg-secondary/30">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground font-mono shrink-0 text-xs">{el.ref}</span>
            <span className="text-foreground text-xs flex-1 break-words">{getElementLabel(el)}</span>
          </div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              className="flex-1 text-xs text-foreground file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              autoFocus
            />
            <button
              onClick={handleCancel}
              className="p-1 rounded bg-secondary text-foreground hover:bg-secondary/80 transition-colors"
              title="Cancel"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      );
    }

    // Text input mode
    return (
      <div className="flex flex-col gap-1.5 px-2 py-1.5 rounded bg-secondary/30">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground font-mono shrink-0 text-xs">{el.ref}</span>
          <span className="text-foreground text-xs flex-1 break-words">{getElementLabel(el)}</span>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter value..."
            className="flex-1 bg-secondary border-0 rounded px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
              if (e.key === 'Escape') handleCancel();
            }}
          />
          <button
            onClick={handleSubmit}
            disabled={!inputValue.trim()}
            className="p-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Send"
          >
            <Send className="h-3 w-3" />
          </button>
          <button
            onClick={handleCancel}
            className="p-1 rounded bg-secondary text-foreground hover:bg-secondary/80 transition-colors"
            title="Cancel"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`group flex items-start gap-2 px-2 py-1 rounded text-xs transition-colors ${
        !el.disabled ? 'hover:bg-secondary/50 cursor-pointer' : ''
      }`}
      onClick={() => !el.disabled && setIsEditing(true)}
    >
      <span className="text-muted-foreground font-mono shrink-0">{el.ref}</span>
      <span className="text-foreground flex-1 break-words">
        {getElementLabel(el)}
        {isFileInput && <span className="text-muted-foreground ml-1">(file)</span>}
      </span>
      {el.disabled ? (
        <span className="text-[10px] px-1 py-0.5 rounded bg-secondary text-muted-foreground">
          disabled
        </span>
      ) : isFileInput ? (
        <Upload className="h-3 w-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
      ) : (
        <TextCursorInput className="h-3 w-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
      )}
    </div>
  );
}

function CategorySection({
  category,
  elements,
  onClickElement,
  onInputSubmit,
  onFileUpload,
}: {
  category: keyof GroupedElements;
  elements: SnapshotElement[];
  onClickElement: (el: SnapshotElement) => void;
  onInputSubmit: (el: SnapshotElement, value: string) => void;
  onFileUpload: (el: SnapshotElement, file: File) => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const config = CATEGORY_CONFIG[category];
  const Icon = config.icon;
  // Clickable: links, buttons, toggles, and "other"
  // Inputs have special handling with inline input field
  // Not clickable: selects (need special UI for option selection)
  const isNavigable = category === 'links' || category === 'buttons' || category === 'toggles' || category === 'other';
  const isInput = category === 'inputs';

  if (elements.length === 0) return null;

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary/50 transition-colors"
      >
        {isOpen ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <Icon className={`h-3.5 w-3.5 ${config.color}`} />
        <span className="text-sm text-foreground flex-1 text-left">{config.label}</span>
        <span className="text-xs text-muted-foreground">{elements.length}</span>
      </button>
      {isOpen && (
        <div className="px-3 pb-2">
          <div className="space-y-0.5">
            {elements.map((el) =>
              isInput ? (
                <InputElement key={el.ref} el={el} onSubmit={onInputSubmit} onFileUpload={onFileUpload} />
              ) : (
                <div
                  key={el.ref}
                  className={`group flex items-start gap-2 px-2 py-1 rounded text-xs transition-colors ${
                    isNavigable && !el.disabled
                      ? 'hover:bg-secondary/50 cursor-pointer'
                      : ''
                  }`}
                  onClick={() => isNavigable && !el.disabled && onClickElement(el)}
                >
                  <span className="text-muted-foreground font-mono shrink-0">{el.ref}</span>
                  <span className="text-foreground flex-1 break-words">{getElementLabel(el)}</span>
                  {/* Show toggle state indicator */}
                  {category === 'toggles' && !el.disabled && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      el.checked
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-secondary text-muted-foreground'
                    }`}>
                      {el.type === 'radio'
                        ? (el.checked ? '●' : '○')
                        : (el.checked ? 'ON' : 'OFF')}
                    </span>
                  )}
                  {el.disabled ? (
                    <span className="text-[10px] px-1 py-0.5 rounded bg-secondary text-muted-foreground">
                      disabled
                    </span>
                  ) : isNavigable ? (
                    <Play className="h-3 w-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  ) : null}
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const PAGE_TYPE_CONFIG = {
  settings: { label: 'Settings', icon: Settings, color: 'text-amber-400' },
  list: { label: 'List', icon: ListOrdered, color: 'text-blue-400' },
  form: { label: 'Form', icon: FormInput, color: 'text-purple-400' },
  dashboard: { label: 'Dashboard', icon: BarChart3, color: 'text-emerald-400' },
  content: { label: 'Content', icon: ContentIcon, color: 'text-muted-foreground' },
} as const;

const REGION_CONFIG: Record<RegionType, { label: string; icon: typeof Globe; color: string; bgColor: string }> = {
  globalHeader: { label: 'Header', icon: Globe, color: 'text-slate-400', bgColor: 'bg-slate-500/10' },
  utilityBar: { label: 'Utility Bar', icon: Columns, color: 'text-zinc-400', bgColor: 'bg-zinc-500/10' },
  sidebar: { label: 'Sidebar', icon: PanelLeft, color: 'text-indigo-400', bgColor: 'bg-indigo-500/10' },
  pageHeader: { label: 'Page Header', icon: Layers, color: 'text-amber-400', bgColor: 'bg-amber-500/10' },
  tabStrip: { label: 'Tabs', icon: LayoutGrid, color: 'text-cyan-400', bgColor: 'bg-cyan-500/10' },
  mainContent: { label: 'Main Content', icon: AppWindow, color: 'text-emerald-400', bgColor: 'bg-emerald-500/10' },
  chatWidget: { label: 'Chat Widget', icon: MessageSquare, color: 'text-violet-400', bgColor: 'bg-violet-500/10' },
  footer: { label: 'Footer', icon: Columns, color: 'text-zinc-400', bgColor: 'bg-zinc-500/10' },
  modal: { label: 'Modal', icon: AppWindow, color: 'text-rose-400', bgColor: 'bg-rose-500/10' },
};

type ViewMode = 'region' | 'type';

function RegionSection({
  region,
  elements,
  onClickElement,
  onInputSubmit,
  onFileUpload,
}: {
  region: Region;
  elements: SnapshotElement[];
  onClickElement: (el: SnapshotElement) => void;
  onInputSubmit: (el: SnapshotElement, value: string) => void;
  onFileUpload: (el: SnapshotElement, file: File) => void;
}) {
  const [isOpen, setIsOpen] = useState(region.type === 'mainContent' || region.type === 'modal');
  const config = REGION_CONFIG[region.type];
  const Icon = config.icon;

  // Sub-group elements by category within the region
  const subGroups = useMemo(() => {
    const groups: GroupedElements = { buttons: [], links: [], toggles: [], inputs: [], selects: [], other: [] };
    for (const el of elements) {
      groups[categorizeElement(el)].push(el);
    }
    return groups;
  }, [elements]);

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary/50 transition-colors"
      >
        {isOpen ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <Icon className={`h-3.5 w-3.5 ${config.color}`} />
        <span className="text-sm text-foreground flex-1 text-left">{config.label}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${config.bgColor} ${config.color}`}>
          {region.summary}
        </span>
        <span className="text-xs text-muted-foreground">{elements.length}</span>
      </button>
      {isOpen && (
        <div className="pl-2">
          {(Object.keys(CATEGORY_CONFIG) as (keyof GroupedElements)[]).map((cat) => {
            const catElements = subGroups[cat];
            if (catElements.length === 0) return null;
            return (
              <CategorySection
                key={cat}
                category={cat}
                elements={catElements}
                onClickElement={onClickElement}
                onInputSubmit={onInputSubmit}
                onFileUpload={onFileUpload}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function PageContextBar({ context }: { context: PageContext }) {
  const pageType = context.pageType ? PAGE_TYPE_CONFIG[context.pageType] : null;
  const PageTypeIcon = pageType?.icon;

  return (
    <div className="space-y-1.5">
      {/* Subject line */}
      {context.subject && (
        <div className="flex items-center gap-2">
          <Compass className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="text-sm font-medium text-foreground truncate" title={context.subject}>
            {context.subject}
          </span>
        </div>
      )}

      {/* Tags row: page type, active tab, nav intent */}
      <div className="flex flex-wrap items-center gap-1.5">
        {pageType && PageTypeIcon && (
          <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-secondary ${pageType.color}`}>
            <PageTypeIcon className="h-3 w-3" />
            {pageType.label}
          </span>
        )}
        {context.activeTab && (
          <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-secondary text-cyan-400">
            <LayoutGrid className="h-3 w-3" />
            {context.activeTab}
          </span>
        )}
        {context.navigationIntent && (
          <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-secondary text-violet-400" title={`Navigated via: ${context.navigationIntent.label}`}>
            <MapPin className="h-3 w-3" />
            {context.navigationIntent.label}
          </span>
        )}
      </div>

      {/* Breadcrumbs */}
      {context.breadcrumbs && context.breadcrumbs.length > 0 && (
        <div className="text-[11px] text-muted-foreground truncate">
          {context.breadcrumbs.join(' / ')}
        </div>
      )}

      {/* URL path */}
      <div className="text-[11px] text-muted-foreground font-mono truncate" title={context.url}>
        {context.path}
      </div>
    </div>
  );
}

export function PageAnalysis() {
  const { currentSnapshot, snapshotPending, activeTabId, requestAction } = useBrowserStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('region');

  const hasRegions = (currentSnapshot?.regions?.length ?? 0) > 0;

  const grouped = useMemo<GroupedElements>(() => {
    if (!currentSnapshot) {
      return {
        buttons: [],
        links: [],
        toggles: [],
        inputs: [],
        selects: [],
        other: [],
      };
    }

    const groups: GroupedElements = {
      buttons: [],
      links: [],
      toggles: [],
      inputs: [],
      selects: [],
      other: [],
    };

    for (const el of currentSnapshot.elements) {
      const category = categorizeElement(el);
      groups[category].push(el);
    }

    return groups;
  }, [currentSnapshot]);

  // Filter elements based on search query
  const filteredGroups = useMemo<GroupedElements>(() => {
    if (!searchQuery.trim()) {
      return grouped;
    }

    const query = searchQuery.toLowerCase();
    const filterElements = (elements: SnapshotElement[]) =>
      elements.filter((el) => {
        const label = getElementLabel(el).toLowerCase();
        const text = (el.text || '').toLowerCase();
        const name = (el.name || '').toLowerCase();
        const ariaLabel = (el.ariaLabel || '').toLowerCase();
        return (
          label.includes(query) ||
          text.includes(query) ||
          name.includes(query) ||
          ariaLabel.includes(query) ||
          el.ref.toLowerCase().includes(query)
        );
      });

    return {
      buttons: filterElements(grouped.buttons),
      links: filterElements(grouped.links),
      toggles: filterElements(grouped.toggles),
      inputs: filterElements(grouped.inputs),
      selects: filterElements(grouped.selects),
      other: filterElements(grouped.other),
    };
  }, [grouped, searchQuery]);

  const totalElements = currentSnapshot?.elements.length ?? 0;
  const filteredCount =
    filteredGroups.buttons.length +
    filteredGroups.links.length +
    filteredGroups.toggles.length +
    filteredGroups.inputs.length +
    filteredGroups.selects.length +
    filteredGroups.other.length;

  // Group elements by region
  const regionGrouped = useMemo(() => {
    if (!currentSnapshot?.regions || !currentSnapshot.elements) return new Map<RegionType, SnapshotElement[]>();

    const map = new Map<RegionType, SnapshotElement[]>();
    const query = searchQuery.toLowerCase();

    for (const el of currentSnapshot.elements) {
      if (!el.region) continue;
      // Apply search filter
      if (query) {
        const label = getElementLabel(el).toLowerCase();
        const text = (el.text || '').toLowerCase();
        const name = (el.name || '').toLowerCase();
        const ariaLabel = (el.ariaLabel || '').toLowerCase();
        if (!label.includes(query) && !text.includes(query) && !name.includes(query) && !ariaLabel.includes(query) && !el.ref.toLowerCase().includes(query)) {
          continue;
        }
      }
      const existing = map.get(el.region);
      if (existing) {
        existing.push(el);
      } else {
        map.set(el.region, [el]);
      }
    }
    return map;
  }, [currentSnapshot, searchQuery]);

  // Track which element needs a file upload
  const [fileUploadElement, setFileUploadElement] = useState<SnapshotElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if a button is a file upload trigger
  const isFileUploadButton = (el: SnapshotElement): boolean => {
    const label = getElementLabel(el).toLowerCase();
    const fileKeywords = ['choose file', 'choose files', 'upload', 'browse', 'select file', 'add file', 'attach'];
    return fileKeywords.some(keyword => label.includes(keyword));
  };

  const handleClickElement = (el: SnapshotElement) => {
    if (!activeTabId) return;

    const label = getElementLabel(el);
    const category = categorizeElement(el);

    // Clickable: links, buttons, toggles, other
    // Inputs have separate handler
    // Blocked: selects (need special UI for option selection)
    if (category !== 'links' && category !== 'buttons' && category !== 'toggles' && category !== 'other') {
      return;
    }

    // For file upload buttons, show file picker instead of clicking
    if (category === 'buttons' && isFileUploadButton(el)) {
      setFileUploadElement(el);
      // Trigger file input click
      setTimeout(() => fileInputRef.current?.click(), 0);
      return;
    }

    // Build appropriate preview message
    let preview: string;
    if (category === 'toggles') {
      if (el.type === 'radio') {
        preview = `Select "${label}"`;
      } else {
        const currentState = el.checked ? 'ON' : 'OFF';
        const newState = el.checked ? 'OFF' : 'ON';
        preview = `Toggle "${label}" (${currentState} → ${newState})`;
      }
    } else if (category === 'buttons') {
      preview = `Click "${label}"`;
    } else {
      preview = `Navigate to "${label}"`;
    }

    const action: BrowserAction = {
      type: 'click',
      ref: el.ref,
      preview,
      elementInfo: {
        tag: el.tag,
        text: el.text,
        ariaLabel: el.ariaLabel,
        name: el.name,
      },
    };

    requestAction(activeTabId, action);
  };

  // Handle file selection for file upload buttons
  const handleButtonFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !fileUploadElement || !activeTabId) {
      setFileUploadElement(null);
      return;
    }

    const label = getElementLabel(fileUploadElement);

    // Convert file to base64
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64Data = result.split(',')[1] || result;
        resolve(base64Data);
      };
      reader.readAsDataURL(file);
    });

    const action: BrowserAction = {
      type: 'upload',
      ref: fileUploadElement.ref,
      preview: `Upload "${file.name}" via "${label}"`,
      elementInfo: {
        tag: fileUploadElement.tag,
        text: fileUploadElement.text,
        ariaLabel: fileUploadElement.ariaLabel,
        name: fileUploadElement.name,
      },
      fileData: {
        name: file.name,
        type: file.type,
        base64,
      },
    };

    requestAction(activeTabId, action);
    setFileUploadElement(null);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleInputSubmit = (el: SnapshotElement, value: string) => {
    if (!activeTabId) return;

    const label = getElementLabel(el);
    const action: BrowserAction = {
      type: 'type',
      ref: el.ref,
      value,
      preview: `Type "${value}" into "${label}"`,
      elementInfo: {
        tag: el.tag,
        text: el.text,
        ariaLabel: el.ariaLabel,
        name: el.name,
      },
    };

    requestAction(activeTabId, action);
  };

  const handleFileUpload = async (el: SnapshotElement, file: File) => {
    if (!activeTabId) return;

    const label = getElementLabel(el);

    // Convert file to base64
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64Data = result.split(',')[1] || result;
        resolve(base64Data);
      };
      reader.readAsDataURL(file);
    });

    const action: BrowserAction = {
      type: 'upload',
      ref: el.ref,
      preview: `Upload "${file.name}" to "${label}"`,
      elementInfo: {
        tag: el.tag,
        text: el.text,
        ariaLabel: el.ariaLabel,
        name: el.name,
      },
      fileData: {
        name: file.name,
        type: file.type,
        base64,
      },
    };

    requestAction(activeTabId, action);
  };

  return (
    <div className="bg-card border border-border rounded-lg flex flex-col flex-1 min-h-0">
      {/* Hidden file input for button file uploads */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleButtonFileSelect}
      />

      {/* Header */}
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-foreground">Page Analysis</h2>
          {currentSnapshot && (
            <span className="text-xs text-muted-foreground">
              {searchQuery ? `${filteredCount} of ` : ''}{totalElements} elements
            </span>
          )}
        </div>
        {currentSnapshot?.pageContext ? (
          <div className="mt-2">
            <PageContextBar context={currentSnapshot.pageContext} />
          </div>
        ) : currentSnapshot ? (
          <p className="text-sm text-foreground mt-1 truncate" title={currentSnapshot.title}>
            {currentSnapshot.title}
          </p>
        ) : null}
      </div>

      {/* Search + View Toggle */}
      {currentSnapshot && (
        <div className="px-3 py-2 border-b border-border space-y-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search elements..."
              className="w-full bg-secondary border-0 rounded pl-7 pr-7 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {hasRegions && (
            <div className="flex gap-1">
              <button
                onClick={() => setViewMode('region')}
                className={`text-[11px] px-2 py-1 rounded transition-colors ${
                  viewMode === 'region'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                By Region
              </button>
              <button
                onClick={() => setViewMode('type')}
                className={`text-[11px] px-2 py-1 rounded transition-colors ${
                  viewMode === 'type'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                By Type
              </button>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {snapshotPending ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">Analyzing page...</p>
            </div>
          </div>
        ) : !currentSnapshot ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <FileText className="h-8 w-8 mx-auto text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground mt-2">No snapshot</p>
              <p className="text-xs text-muted-foreground">
                Request a snapshot to analyze the page
              </p>
            </div>
          </div>
        ) : (
          <div>
            {filteredCount === 0 && searchQuery ? (
              <div className="flex items-center justify-center h-20">
                <p className="text-sm text-muted-foreground">No elements match "{searchQuery}"</p>
              </div>
            ) : viewMode === 'region' && hasRegions ? (
              <>
                {currentSnapshot.regions!.map((region) => {
                  const els = regionGrouped.get(region.type) || [];
                  if (els.length === 0 && searchQuery) return null;
                  return (
                    <RegionSection
                      key={region.type}
                      region={region}
                      elements={els}
                      onClickElement={handleClickElement}
                      onInputSubmit={handleInputSubmit}
                      onFileUpload={handleFileUpload}
                    />
                  );
                })}
              </>
            ) : (
              <>
                <CategorySection category="buttons" elements={filteredGroups.buttons} onClickElement={handleClickElement} onInputSubmit={handleInputSubmit} onFileUpload={handleFileUpload} />
                <CategorySection category="toggles" elements={filteredGroups.toggles} onClickElement={handleClickElement} onInputSubmit={handleInputSubmit} onFileUpload={handleFileUpload} />
                <CategorySection category="inputs" elements={filteredGroups.inputs} onClickElement={handleClickElement} onInputSubmit={handleInputSubmit} onFileUpload={handleFileUpload} />
                <CategorySection category="selects" elements={filteredGroups.selects} onClickElement={handleClickElement} onInputSubmit={handleInputSubmit} onFileUpload={handleFileUpload} />
                <CategorySection category="links" elements={filteredGroups.links} onClickElement={handleClickElement} onInputSubmit={handleInputSubmit} onFileUpload={handleFileUpload} />
                <CategorySection category="other" elements={filteredGroups.other} onClickElement={handleClickElement} onInputSubmit={handleInputSubmit} onFileUpload={handleFileUpload} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
